import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy, Crown, Medal, Clock, MessageSquare, Coins, Radio, Gift, Calendar } from "lucide-react";

const METRICS = [
  {
    id: "watch_time",
    label: "Most Watch Time",
    short: "Watch Time",
    icon: Clock,
    color: "#22D3EE",
    subject: "Viewer",
    tagline: "Total minutes spent tuned in across every channel on MystiCast.",
    formatValue: (v) => `${(v || 0).toLocaleString()} min`,
  },
  {
    id: "messages",
    label: "Most Messages Sent",
    short: "Chat Kings",
    icon: MessageSquare,
    color: "#A78BFA",
    subject: "Chatter",
    tagline: "Community chatters who never stop talking. Every message counts.",
    formatValue: (v) => `${(v || 0).toLocaleString()} msgs`,
  },
  {
    id: "top_tippers",
    label: "Top Tippers",
    short: "Big Spenders",
    icon: Coins,
    color: "#FFD700",
    subject: "Supporter",
    tagline: "The Gold Coin heavyweights — total tips sent to verified streamers.",
    formatValue: (v) => `${(v || 0).toLocaleString()} GC`,
  },
  {
    id: "top_streamers",
    label: "Top Streamers",
    short: "Top Streamers",
    icon: Radio,
    color: "#FF3366",
    subject: "Streamer",
    tagline: "Streamers whose channels have accumulated the most watch minutes.",
    formatValue: (v) => `${(v || 0).toLocaleString()} min watched`,
  },
];

const RANK_VISUALS = [
  { icon: Crown, color: "#F59E0B", label: "1st" },
  { icon: Medal, color: "#A1A1AA", label: "2nd" },
  { icon: Medal, color: "#B45309", label: "3rd" },
];

export default function Leaderboard() {
  const [metric, setMetric] = useState("watch_time");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rewardsInfo, setRewardsInfo] = useState(null);
  const [payouts, setPayouts] = useState([]);

  useEffect(() => {
    api.get("/leaderboard/rewards").then(r => setRewardsInfo(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/leaderboard?metric=${metric}`).then(r => r.data.entries || []).catch(() => []),
      api.get(`/leaderboard/payouts?metric=${metric}&limit=1`).then(r => r.data || []).catch(() => []),
    ]).then(([entries, past]) => {
      setRows(entries);
      setPayouts(past);
    }).finally(() => setLoading(false));
  }, [metric]);

  const active = METRICS.find(m => m.id === metric) || METRICS[0];
  const ActiveIcon = active.icon;
  const rewards = rewardsInfo?.rewards || [
    { place: 1, gold_coins: 1000, sparks: 2500 },
    { place: 2, gold_coins: 500,  sparks: 1250 },
    { place: 3, gold_coins: 250,  sparks: 500  },
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
      <div className="sc-label" style={{ color: active.color }}>Engagement Leaderboards</div>
      <h1 className="sc-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1 mb-2">Community Champions</h1>
      <p className="text-sm text-zinc-400 mb-6 max-w-2xl">
        The players and creators who make MystiCast alive. Rankings reflect real engagement — watch time, chat activity, tipping, and stream popularity — not on-site game wins.
      </p>

      {/* Monthly Rewards banner */}
      <div
        data-testid="leaderboard-rewards-banner"
        className="glass rounded-md p-5 mb-6 border"
        style={{ borderColor: "rgba(255,215,0,0.25)", background: "linear-gradient(180deg,rgba(255,215,0,0.05),transparent 60%)" }}
      >
        <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-[#FFD700]" />
            <div className="sc-heading text-lg font-bold text-[#FFD700]">Monthly Rewards</div>
          </div>
          {rewardsInfo && (
            <div className="flex items-center gap-2 text-xs sc-mono text-zinc-400" data-testid="leaderboard-cycle-info">
              <Calendar className="w-3.5 h-3.5" />
              <span>Cycle: {rewardsInfo.current_month}</span>
              <span className="text-zinc-600">·</span>
              <span>Payout in <span className="text-white font-bold">{rewardsInfo.days_remaining_in_cycle}d</span></span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {rewards.map((r, i) => {
            const RV = RANK_VISUALS[i];
            return (
              <div
                key={r.place}
                data-testid={`reward-place-${r.place}`}
                className="border border-white/10 rounded-sm p-3 flex items-center gap-3"
                style={{ background: "rgba(0,0,0,0.35)" }}
              >
                <RV.icon className="w-6 h-6 flex-shrink-0" style={{ color: RV.color }} />
                <div className="min-w-0 flex-1">
                  <div className="sc-mono text-[10px] uppercase tracking-widest" style={{ color: RV.color }}>
                    {RV.label} Place
                  </div>
                  <div className="flex items-baseline gap-2 flex-wrap mt-0.5">
                    <span className="text-sm font-bold text-[#FFD700]">{r.gold_coins.toLocaleString()} GC</span>
                    <span className="text-xs text-zinc-500">+</span>
                    <span className="text-sm font-bold text-[color:var(--sc-cyan)]">{r.sparks.toLocaleString()} Sparks</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-[11px] text-zinc-500 mt-3">
          Every category pays out the top 3 automatically at the start of each new UTC month, based on the activity you accumulated during the previous month.
        </div>
      </div>

      {/* Metric tabs */}
      <div className="flex gap-2 flex-wrap mb-6">
        {METRICS.map(m => {
          const Icon = m.icon;
          const isActive = metric === m.id;
          return (
            <button
              key={m.id}
              data-testid={`leaderboard-tab-${m.id}`}
              onClick={() => setMetric(m.id)}
              className={`px-3 py-2 text-xs uppercase tracking-widest font-bold rounded-sm border transition flex items-center gap-2 ${
                isActive ? "bg-white/[0.04]" : "text-zinc-400 hover:border-white/30"
              }`}
              style={isActive ? { borderColor: m.color, color: m.color } : { borderColor: "rgba(255,255,255,0.08)" }}
            >
              <Icon className="w-3.5 h-3.5" /> {m.short}
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex items-center gap-3 text-xs text-zinc-400" data-testid="leaderboard-tagline">
        <ActiveIcon className="w-4 h-4" style={{ color: active.color }} />
        <span>{active.tagline}</span>
      </div>

      {/* Last month winners */}
      {payouts.length > 0 && payouts[0]?.winners?.length > 0 && (
        <div className="mb-4 p-4 border border-white/10 rounded-md flex items-center gap-3 text-xs flex-wrap" data-testid="leaderboard-last-winners">
          <span className="sc-mono uppercase tracking-widest text-zinc-500">Last month ({payouts[0].month}) winners:</span>
          {payouts[0].winners.map(w => (
            <span key={w.username} className="inline-flex items-center gap-1.5 text-zinc-300">
              <Crown className="w-3 h-3" style={{ color: RANK_VISUALS[w.place - 1].color }} />
              <Link to={`/c/${w.username}`} className="font-bold hover:underline">{w.username}</Link>
              <span className="text-zinc-500">({w.place === 1 ? "1st" : w.place === 2 ? "2nd" : "3rd"})</span>
            </span>
          ))}
        </div>
      )}

      <div className="border border-white/10 rounded-md overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 text-xs sc-label border-b border-white/10 bg-black/30">
          <div className="col-span-2">Rank</div>
          <div className="col-span-6">{active.subject}</div>
          <div className="col-span-4 text-right">{active.short}</div>
        </div>
        {loading && <div className="p-6 text-center text-zinc-400 text-sm">Loading...</div>}
        {!loading && rows.length === 0 && (
          <div className="p-10 text-center">
            <Trophy className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <div className="sc-heading text-lg font-bold text-zinc-300">No entries yet</div>
            <div className="text-xs text-zinc-500 mt-1">Be the first to make this board light up.</div>
            <Link to="/live" className="sc-btn text-xs inline-block mt-4">Explore streams</Link>
          </div>
        )}
        {rows.map((r, i) => {
          const RV = RANK_VISUALS[i];
          const reward = rewards[i];
          return (
            <div
              data-testid={`leaderboard-row-${i}`}
              key={r.username + i}
              className="grid grid-cols-12 px-4 py-3 items-center border-b border-white/5 hover:bg-white/[0.02]"
              style={RV ? { background: `linear-gradient(90deg, ${RV.color}0d, transparent 40%)` } : undefined}
            >
              <div className="col-span-2 flex items-center gap-2">
                {RV ? (
                  <RV.icon className="w-5 h-5" style={{ color: RV.color }} />
                ) : (
                  <span className="text-zinc-500 text-sm font-bold w-5 text-center">{i + 1}</span>
                )}
              </div>
              <div className="col-span-6 min-w-0">
                <Link to={`/c/${r.username}`} className="text-sm font-semibold hover:text-[#FF3366] block truncate">{r.username}</Link>
                {reward && (
                  <div className="text-[10px] sc-mono text-zinc-500 mt-0.5" data-testid={`row-reward-hint-${i}`}>
                    Earning <span className="text-[#FFD700]">{reward.gold_coins} GC</span>
                    <span className="text-zinc-600 mx-1">+</span>
                    <span className="text-[color:var(--sc-cyan)]">{reward.sparks} Sparks</span> this month
                  </div>
                )}
              </div>
              <div className="col-span-4 text-right text-sm font-bold" style={{ color: active.color }}>
                {active.formatValue(r.value)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
